// The CM step and Changes. The CM step is the project's setup at its contract manufacturer only (Mitch, 2026-11-04: a
// project is set up once, the work is on its runs): its CM site, how often it's counted, and what of this project's is
// there (the Consignment page's own numbers, summarizeCms()), each site opening Consignment, where its stock, shipments
// and counts are. Make orders are on each run's Orders (and Consignment's Make orders). Changes: the change notices that
// touch the project's parts. Each list is the usual table (DataTable: views, columns, Copy / CSV).
import { ArrowRight } from "lucide-react";
import { useMemo } from "react";
import { COUNT_POLICIES, COUNT_POLICY_LABELS, type CountPolicy } from "../../../../shared/consignment.ts";
import type { Location } from "../../../../shared/db.gen.ts";
import type { Readiness } from "../../../../shared/projects.ts";
import { money, plural } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { Link, navigate } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Select } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { PageSection } from "../../ui/PageParts.tsx";
import { useCms } from "../consignment/lib.ts";
import { StepGaps, type TabProps } from "./Workspace.tsx";

/** A CM site of the project's CM, with what of this project's is there. */
type SiteRow = { id: string; site: Location | undefined; name: string; parts: number; value: number; noCost: number };

export function ProductionTab({ project, px, scope, r, here }: TabProps) {
  const cms = useCms();
  const locations = useRows("location");
  const cm = project.cm_vendor_id ? px.vendorById.get(project.cm_vendor_id) : undefined;
  const inScope = (id: string) => scope.parts.has(id) || scope.variantIds.has(id);
  const rows: SiteRow[] = r.cmSites.map((site) => {
    const stock = (cms.get(site.id)?.stock ?? []).filter((s) => inScope(s.itemId) && (s.onHand || s.inTransit));
    return {
      id: site.id,
      site: locations.find((l) => l.id === site.id),
      name: site.name,
      parts: stock.length,
      value: stock.reduce((n, s) => n + (s.value ?? 0), 0),
      noCost: stock.filter((s) => s.value == null).length,
    };
  });
  const editable = can("location.update_cm");
  const columns: Column<SiteRow>[] = [
    { key: "name", header: "Site", width: "minmax(180px,1fr)", value: (x) => x.name, cell: (x) => <span className="truncate font-medium">{x.name}</span> },
    {
      key: "count_policy",
      header: "Counted",
      headerTitle: "How often HHL Ops asks for a count of HHL's parts there",
      width: "190px",
      value: (x) => (x.site ? COUNT_POLICY_LABELS[x.site.count_policy] : ""),
      cell: (x) =>
        !x.site ? null : editable ? (
          // A choice in the row: its click mustn't open the row
          <span role="presentation" className="w-full" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <Select
              aria-label={`How often ${x.name} is counted`}
              value={x.site.count_policy}
              onChange={(e) => x.site && mutate("location.update_cm", { id: x.site.id, patch: { count_policy: e.target.value as CountPolicy } })}
              options={COUNT_POLICIES.map((c) => ({ value: c, label: COUNT_POLICY_LABELS[c] }))}
              className="h-7 text-[12px]"
            />
          </span>
        ) : (
          <Badge>{COUNT_POLICY_LABELS[x.site.count_policy]}</Badge>
        ),
    },
    {
      key: "parts",
      header: "This project's parts there",
      width: "minmax(200px,0.8fr)",
      value: (x) => x.value,
      text: (x) => (x.parts ? `${plural(x.parts, "part")} · ${money(Math.round(x.value))}` : "None yet"),
      cell: (x) =>
        x.parts ? (
          <span className="truncate text-[12px]">
            {plural(x.parts, "part")} · <span className="font-mono">{money(Math.round(x.value))}</span>
            {x.noCost ? <span className="text-warn"> ({x.noCost} without a cost)</span> : null}
          </span>
        ) : (
          <span className="text-[12px] text-faint">None yet</span>
        ),
    },
    {
      key: "open",
      header: "",
      width: "130px",
      cell: (x) => (
        <Link to={`/consignment?cm=${x.id}`} className="flex items-center gap-1 text-[12px] text-accent-text hover:underline">
          Consignment <ArrowRight className="size-3" />
        </Link>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-3 p-4">
      <StepGaps r={r} steps={["production"]} project={project} px={px} here={here} />
      <PageSection
        title="CM site"
        note={`Where ${cm ? cm.short_name || cm.name : "the CM"} keeps HHL's parts, made with it (named after it). Its stock, shipments and counts are on Consignment; its make orders on each run.`}
      >
        {(bar) => (
          <DataTable
            fit
            rows={rows}
            columns={columns}
            onOpen={(x) => navigate(`/consignment?cm=${x.id}`)}
            exportName="CM sites"
            bar={bar}
            pageFilters={false}
            rowNoun={["site", "sites"]}
            empty={
              <div className="px-4 py-3 text-muted">
                {cm ? (
                  <>{cm.short_name || cm.name}'s site is made as soon as it's the project's CM.</>
                ) : (
                  <>
                    Choose the project's contract manufacturer in{" "}
                    <Link to={here.split("?")[0]} className="text-accent-text hover:underline">
                      Basics
                    </Link>{" "}
                    first.
                  </>
                )}
              </div>
            }
          />
        )}
      </PageSection>
    </div>
  );
}

type NoticeRow = Readiness["notices"][number];

export function ChangesTab({ scope, r }: TabProps) {
  const links = useRows("change_notice_item");
  const items = useRows("item");
  const skuOf = useMemo(() => new Map(items.map((i) => [i.id, i.sku || i.name])), [items]);
  const inScope = (id: string) => scope.parts.has(id) || scope.variantIds.has(id);
  const touched = (n: NoticeRow) =>
    links
      .filter((l) => l.change_notice_id === n.id && inScope(l.item_id))
      .map((l) => skuOf.get(l.item_id) ?? "")
      .join(", ");
  const effective = (n: NoticeRow) => (n.effective === "date" ? `from ${n.effective_date ?? "?"}` : "from the next PO");
  const columns: Column<NoticeRow>[] = [
    {
      key: "number",
      header: "Notice",
      width: "90px",
      value: (n) => Number(String(n.number).replace(/\D/g, "")) || null,
      text: (n) => n.number,
      cell: (n) => <span className="font-mono text-[12px]">{n.number}</span>,
    },
    { key: "title", header: "Title", width: "minmax(200px,1fr)" },
    {
      key: "status",
      header: "Status",
      width: "100px",
      value: (n) => (n.status === "released" ? "Released" : "Draft"),
      cell: (n) => <Badge tone={n.status === "released" ? "ok" : "accent"}>{n.status === "released" ? "Released" : "Draft"}</Badge>,
    },
    { key: "effective", header: "Effective", width: "150px", value: effective, cell: (n) => <span className="text-[12px] text-muted">{effective(n)}</span> },
    {
      key: "touches",
      header: "Touches",
      headerTitle: "The project's parts and variants it changes",
      width: "minmax(160px,1fr)",
      value: touched,
      cell: (n) => (
        <span className="truncate font-mono text-[11px] text-muted" title={touched(n)}>
          {touched(n)}
        </span>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-3 p-4">
      <PageSection
        title="Change notices"
        note="Every change notice that touches one of the project's parts or variants"
        actions={
          <Link to="/specs?tab=ecn" className="flex items-center gap-1 text-[12px] text-accent-text hover:underline">
            Specs <ArrowRight className="size-3" />
          </Link>
        }
      >
        {(slot) => (
          <DataTable
            fit
            rows={r.notices}
            columns={columns}
            // Newest first, as readiness lists them
            defaultSort={{ key: "number", desc: true }}
            onOpen={(n) => navigate(`/specs?tab=ecn&open=${n.id}`)}
            exportName="Change notices"
            bar={slot}
            pageFilters={false}
            rowNoun={["change notice", "change notices"]}
            empty="No change notice touches this project's parts yet."
          />
        )}
      </PageSection>
    </div>
  );
}
